import { ChevronRight } from '@mui/icons-material'
import { Box, Card, Stack, Typography } from '@mui/material'
import { type FC, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

interface StatCardProps {
    label: string
    value: ReactNode
    sublabel?: ReactNode
    // When set, the whole card becomes a router link to this path and
    // shows hover/focus affordances. Used by Judge verdicts, Feedback,
    // and Errors cards to drill into a pre-filtered list.
    to?: string
    testId?: string
}

export const StatCard: FC<StatCardProps> = ({
    label,
    value,
    sublabel,
    to,
    testId,
}) => {
    const interactive = Boolean(to)
    const cardSx = {
        p: 2,
        height: '100%',
        position: 'relative' as const,
        ...(interactive
            ? {
                  textDecoration: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  transition:
                      'border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease',
                  '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 2,
                  },
                  '&:focus-visible': {
                      outline: 'none',
                      borderColor: 'primary.main',
                      boxShadow: 3,
                  },
                  '&:hover .StatCard-chevron, &:focus-visible .StatCard-chevron':
                      {
                          opacity: 1,
                          transform: 'translateX(0)',
                      },
              }
            : {}),
    }

    return (
        <Card
            variant="outlined"
            component={interactive ? Link : 'div'}
            to={interactive ? to : undefined}
            sx={cardSx}
            data-testid={testId}
        >
            <Stack spacing={0.5}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        textTransform: 'uppercase',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                    }}
                >
                    {label}
                </Typography>
                <Box
                    sx={{
                        fontSize: '1.5rem',
                        fontWeight: 600,
                        lineHeight: 1.2,
                    }}
                >
                    {value}
                </Box>
                {sublabel ? (
                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {sublabel}
                    </Box>
                ) : null}
            </Stack>
            {interactive ? (
                <Box
                    className="StatCard-chevron"
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'text.secondary',
                        opacity: 0.35,
                        transform: 'translateX(-2px)',
                        transition: 'opacity 150ms ease, transform 150ms ease',
                        display: 'flex',
                    }}
                >
                    <ChevronRight fontSize="small" />
                </Box>
            ) : null}
        </Card>
    )
}
